import type { ApplicationInsights, Exception, IConfig, IConfiguration, IDependencyTelemetry } from '@microsoft/applicationinsights-web'

// The App Insights SDK (~74 KB gzip) is loaded after hydration instead of from the
// entry, so the first screen does not wait for it. Until it loads, $appInsights is
// this stand-in:
// - The track calls and the authenticated user are queued, and replayed in order once
//   the SDK is there. A replayed call is sent with the time it was made (the SDK takes
//   the envelope time from baseData.startTime only), and a queued page view keeps the
//   page it was made on (the SDK would fill the uri from the location at replay).
// - Telemetry initializers are queued and added before the SDK starts.
// - withCore runs its callback with the SDK core, at replay for one made before the
//   load, in order with the track calls: a trace started for a page applies to that
//   page's replayed page view.
// - onunloadFlush is passed to the SDK once it is there. One asked for before that runs
//   right after the replay, which a page that unloads first never reaches.
// - What the SDK collects by itself is recorded the way it would be: uncaught errors
//   (a window error listener, replayed through the SDK's own window.onerror path,
//   CreateAutoException then _onerror) and fetch requests
//   (window.fetch is wrapped, as the SDK does, and each request is replayed as a
//   dependency, under the SDK's disableFetchTracking / excludeRequestFromAutoTrackingPatterns
//   rules; maxAjaxCallsPerView is applied by the SDK on replay). XMLHttpRequest made
//   before the SDK loads is not recorded.
// - onLoaded runs a callback once the SDK has started, which is when its ai_user cookie
//   exists. ai_session is written when the SDK processes its first item, so it exists
//   then only if something was queued (the first page view nearly always is).
// The queue holds at most MAX_QUEUED calls while the SDK is slow to load; the oldest
//   are dropped first.
// A visitor who leaves before the SDK loads sends nothing from the queue. If the SDK
// cannot load, or there is no connection string (local dev), the queue is dropped and
// $appInsights stays a no-op.
// app/utils/tests/deferredAppInsights.contract.spec.ts checks the replay against the
// real SDK.

export type AppInsightsConfig = IConfiguration & IConfig

const FORWARDED = ['trackTrace', 'trackException', 'trackEvent', 'trackPageView', 'trackDependencyData', 'setAuthenticatedUserContext'] as const
type Forwarded = typeof FORWARDED[number]
type Initializer = Parameters<ApplicationInsights['addTelemetryInitializer']>[0]
type Core = ApplicationInsights['core']

// What the stand-in implements, and no more. Code typed with it cannot call an SDK
// member it does not forward. useNuxtApp().$appInsights itself is typed
// TelemetryClient & DeferredAppInsights (the module's server plugin provides a
// TelemetryClient), so a TelemetryClient member still compiles on it and throws in
// the browser.
export type DeferredAppInsights = Pick<ApplicationInsights, Forwarded> & {
  onunloadFlush: () => void
  addTelemetryInitializer: (initializer: Initializer) => void
  withCore: (run: (core: Core) => void) => void
  // Runs at once when the SDK has started, cannot load, or is not configured.
  onLoaded: (callback: () => void) => void
}

export type AppInsightsSdk = Pick<ApplicationInsights, Forwarded
  | 'onunloadFlush' | 'addTelemetryInitializer' | 'loadAppInsights' | '_onerror' | 'core'>

type SdkModule = {
  ApplicationInsights: new (snippet: { config: AppInsightsConfig }) => AppInsightsSdk
  Exception: Pick<typeof Exception, 'CreateAutoException'>
}

// A bound for a load that stalls while the page keeps making calls (an error storm).
export const MAX_QUEUED = 500

const randomHex = (bytes: number) =>
  Array.from(crypto.getRandomValues(new Uint8Array(bytes)), byte => byte.toString(16).padStart(2, '0')).join('')

// The SDK reports a fetch as "<METHOD> <URL as passed to fetch>", targeting the absolute
// URL, with the method as passed, this success rule and a "|<trace id>.<span id>" id
// (ajaxRecord).
const dependencyOf = (method: string, requested: string, url: string, start: number, duration: number, status: number): IDependencyTelemetry => ({
  id: `|${randomHex(16)}.${randomHex(8)}`,
  name: `${method.toUpperCase()} ${requested}`,
  target: url,
  type: 'Fetch',
  startTime: new Date(start),
  duration: Math.round(duration * 1000) / 1000,
  success: status >= 200 && status < 400,
  responseCode: status,
  properties: { HttpMethod: method },
})

// The SDK's rule (AjaxMonitor _isDisabledRequest): the patterns are tested against the
// lower-cased request URL as it was passed to fetch.
const isFetchTracked = (config: AppInsightsConfig) => {
  const excluded = (config.excludeRequestFromAutoTrackingPatterns ?? [])
    .map(pattern => typeof pattern === 'string' ? new RegExp(pattern) : pattern)
  return (input: RequestInfo | URL) => {
    const url = (typeof input === 'string' ? input : input instanceof Request ? input.url : '').toLowerCase()
    return !config.disableFetchTracking && !excluded.some(pattern => pattern.test(url))
  }
}

const absoluteUrl = (requested: string, base: string) => {
  try {
    return new URL(requested, base).href
  } catch {
    return undefined
  }
}

// Stays in place after the SDK loads: the SDK then wraps it, and it only passes through.
const recordFetchesUntilLoaded = (
  win: Window,
  config: AppInsightsConfig,
  enqueue: (run: (target: AppInsightsSdk) => void, time?: string) => void,
  isBuffering: () => boolean,
) => {
  const nativeFetch = win.fetch
  if (!nativeFetch) {
    return
  }
  const isTracked = isFetchTracked(config)
  win.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
    const requested = input instanceof Request ? input.url : String(input)
    // A URL that does not parse is left to fetch, which rejects it.
    const url = isBuffering() && isTracked(input) ? absoluteUrl(requested, win.location.href) : undefined
    if (!url) {
      return nativeFetch.call(win, input, init)
    }
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET')
    const start = Date.now()
    const started = performance.now()
    const record = (status: number) => {
      const dependency = dependencyOf(method, requested, url, start, performance.now() - started, status)
      enqueue(target => target.trackDependencyData(dependency), new Date(start).toISOString())
    }
    return nativeFetch.call(win, input, init).then((response) => {
      record(response.status)
      return response
    }, (error: unknown) => {
      record(0)
      throw error
    })
  }
}

export const createDeferredAppInsights = (config: AppInsightsConfig | undefined, win: Window = window) => {
  let sdk: AppInsightsSdk | undefined
  let autoException: SdkModule['Exception']['CreateAutoException'] | undefined
  // Calls are queued until the SDK has started, or for good when it cannot.
  let buffering = Boolean(config?.connectionString)
  const queue: Array<{ time: string, run: (target: AppInsightsSdk) => void }> = []
  const initializers: Initializer[] = []
  const loadedCallbacks: Array<() => void> = []
  let flushAfterReplay = false
  // Set while a queued call is replayed, for the initializer that stamps its time. That
  // works because the SDK runs initializers synchronously inside the track call.
  let replayTime: string | undefined

  const enqueue = (run: (target: AppInsightsSdk) => void, time = new Date().toISOString()) => {
    if (sdk) {
      run(sdk)
    } else if (buffering) {
      queue.push({ time, run })
      if (queue.length > MAX_QUEUED) {
        queue.shift()
      }
    }
  }

  const forwarded = Object.fromEntries(FORWARDED.map(name => [name, (...args: unknown[]) =>
    enqueue(target => (target[name] as (...forwarded: unknown[]) => void)(...args))])) as Pick<ApplicationInsights, Forwarded>
  const client: DeferredAppInsights = {
    ...forwarded,
    trackPageView: (pageView, ...rest) => {
      // A queued view keeps the page it was made on. With nothing to queue (no connection
      // string, or the SDK failed to load) do not read the location at all: a page-view
      // timer can outlive its page, and there is no page to name.
      const href = !sdk && buffering ? win.location?.href : undefined
      return forwarded.trackPageView(href ? { uri: href, ...pageView } : pageView, ...rest)
    },
    onunloadFlush: () => {
      if (sdk) {
        sdk.onunloadFlush()
      } else {
        flushAfterReplay = buffering
      }
    },
    addTelemetryInitializer: (initializer) => {
      if (sdk) {
        sdk.addTelemetryInitializer(initializer)
      } else if (buffering) {
        initializers.push(initializer)
      }
    },
    withCore: run => enqueue(target => run(target.core)),
    onLoaded: (callback) => {
      if (buffering) {
        loadedCallbacks.push(callback)
      } else {
        callback()
      }
    },
  }

  const onError = (event: ErrorEvent) => enqueue(target => autoException
    && target._onerror(autoException(event.message, event.filename, event.lineno, event.colno, event.error, event)))

  if (buffering) {
    win.addEventListener('error', onError)
  }
  if (buffering && config) {
    recordFetchesUntilLoaded(win, config, enqueue, () => buffering)
  }

  const stop = () => {
    buffering = false
    win.removeEventListener('error', onError)
  }
  // The SDK has started, or never will.
  const settle = () => loadedCallbacks.splice(0).forEach(callback => callback())

  const load = async (importSdk: () => Promise<SdkModule>) => {
    if (!buffering || !config) {
      return
    }
    try {
      const { ApplicationInsights, Exception } = await importSdk()
      autoException = (...args) => Exception.CreateAutoException(...args)
      const instance = new ApplicationInsights({ config: { ...config } })
      instance.addTelemetryInitializer((item) => {
        if (replayTime && item.baseData && !item.baseData.startTime) {
          item.baseData.startTime = new Date(replayTime)
        }
        return true
      })
      initializers.forEach(initializer => instance.addTelemetryInitializer(initializer))
      // From here the SDK sees requests and errors itself.
      stop()
      instance.loadAppInsights()
      sdk = instance
    } catch (error) {
      // Without the SDK there is no telemetry to report this through.
      console.warn('App Insights SDK did not load; browser telemetry is off for this page.', error)
      stop()
      queue.length = 0
      settle()
      return
    }
    for (const { time, run } of queue.splice(0)) {
      replayTime = time
      try {
        run(sdk)
      } catch {
        // One bad call must not drop the rest of the queue.
      } finally {
        replayTime = undefined
      }
    }
    if (flushAfterReplay) {
      sdk.onunloadFlush()
    }
    settle()
  }

  return { client, load }
}
